/**
 * Search Results Fetcher
 *
 * Server-side function to fetch search results by product type.
 * Used by the search page route.
 */

import { searchByProductType, type SearchProductType } from '@/lib/algolia/bffSearch';
import type { FilterState, ProductHit, SearchResult } from '@/lib/algolia/types';

export type SearchTab = 'all' | 'watches' | 'straps' | 'eyewear' | 'stores';

/**
 * Map tab name to product type
 */
const tabToProductType: Record<SearchTab, SearchProductType | undefined> = {
  all: undefined, // Search all types
  watches: 'Watch',
  straps: 'Strap',
  eyewear: 'Eyewear',
  stores: undefined, // Different index
};

/**
 * Search result with tab-specific metadata
 */
export interface TabSearchResult extends SearchResult<ProductHit> {
  tab: SearchTab;
}

/**
 * Fetch search results for a specific tab.
 *
 * @param locale - Locale string (e.g., 'us-en')
 * @param tab - The search tab ('all', 'watches', 'straps', 'eyewear', 'stores')
 * @param query - Search query string
 * @param filters - Additional filter state
 * @param hitsPerPage - Number of results per page (default: 24)
 * @returns Search results for the specified tab
 */
export async function fetchSearchResults(
  locale: string,
  tab: SearchTab,
  query: string,
  filters?: Partial<FilterState>,
  hitsPerPage: number = 24
): Promise<TabSearchResult> {
  // Handle stores tab separately (different data source)
  if (tab === 'stores') {
    // TODO: Implement store search when store index is available
    return {
      tab: 'stores',
      hits: [],
      totalHits: 0,
      totalPages: 0,
      page: 1,
      hitsPerPage,
      facets: {},
      processingTimeMs: 0,
    };
  }

  // For 'all' tab, we need to search multiple types
  if (tab === 'all') {
    return fetchAllResults(locale, query, filters, hitsPerPage);
  }

  // Single product type search
  const productType = tabToProductType[tab];
  if (!productType) {
    throw new Error(`Invalid tab: ${tab}`);
  }

  const result = await searchByProductType({
    locale,
    productType,
    filterContext: 'search',
    filters: {
      ...filters,
      query,
    },
    hitsPerPage,
  });

  return {
    ...result,
    tab,
  };
}

/**
 * Fetch combined results from all product types for the "all" tab.
 */
async function fetchAllResults(
  locale: string,
  query: string,
  filters?: Partial<FilterState>,
  hitsPerPage: number = 24
): Promise<TabSearchResult> {
  // For the "all" tab, we show a limited preview from each type
  const previewCount = Math.floor(hitsPerPage / 3);

  const [watchResults, strapResults, eyewearResults] = await Promise.all([
    searchByProductType({
      locale,
      productType: 'Watch',
      filterContext: 'search',
      filters: { ...filters, query },
      hitsPerPage: previewCount,
    }),
    searchByProductType({
      locale,
      productType: 'Strap',
      filterContext: 'search',
      filters: { ...filters, query },
      hitsPerPage: previewCount,
    }),
    searchByProductType({
      locale,
      productType: 'Eyewear',
      filterContext: 'search',
      filters: { ...filters, query },
      hitsPerPage: previewCount,
    }),
  ]);

  // Combine hits (interleaved for better UX)
  const combinedHits: ProductHit[] = [];
  const maxLength = Math.max(
    watchResults.hits.length,
    strapResults.hits.length,
    eyewearResults.hits.length
  );

  for (let i = 0; i < maxLength; i++) {
    if (watchResults.hits[i]) combinedHits.push(watchResults.hits[i] as ProductHit);
    if (strapResults.hits[i]) combinedHits.push(strapResults.hits[i] as ProductHit);
    if (eyewearResults.hits[i]) combinedHits.push(eyewearResults.hits[i] as ProductHit);
  }

  // Total across all types
  const totalHits = watchResults.totalHits + strapResults.totalHits + eyewearResults.totalHits;

  return {
    tab: 'all',
    hits: combinedHits,
    totalHits,
    totalPages: 1, // "All" tab doesn't paginate, user goes to specific tabs
    page: 1,
    hitsPerPage,
    facets: {
      ...watchResults.facets,
      ...strapResults.facets,
      ...eyewearResults.facets,
    },
    processingTimeMs: Math.max(
      watchResults.processingTimeMs,
      strapResults.processingTimeMs,
      eyewearResults.processingTimeMs
    ),
  };
}

/**
 * Valid tabs for validation
 */
export const VALID_TABS: SearchTab[] = ['all', 'watches', 'straps', 'eyewear', 'stores'];

/**
 * Check if a string is a valid search tab
 */
export function isValidTab(tab: string): tab is SearchTab {
  return VALID_TABS.includes(tab as SearchTab);
}
